import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Newspaper } from '@phosphor-icons/react';
import { stockApi, portfolioApi, watchlistApi, newsApi } from '../services/api';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import IndicesTicker from '../components/IndicesTicker';
import WatchlistTicker from '../components/WatchlistTicker';
import FIIDIIWidget from '../components/FIIDIIWidget';
import SectorBreakdown from '../components/SectorBreakdown';
import { formatINR, formatPercent } from '../utils/formatters';

const CHART_AXIS_STYLE = { fontSize: '12px' };
const CHART_TOOLTIP_CONTENT_STYLE = { background: '#12141A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '0' };
const CHART_TOOLTIP_LABEL_STYLE = { color: '#94A3B8', fontFamily: 'JetBrains Mono' };
const CHART_TOOLTIP_ITEM_STYLE = { color: '#00FF66', fontFamily: 'JetBrains Mono' };

export default function Dashboard() {
  const { user, loading } = useAuth();
  const [stocks, setStocks] = useState([]);
  const [portfolio, setPortfolio] = useState(null);
  const [watchlists, setWatchlists] = useState([]);
  const [news, setNews] = useState([]);
  const [chartData] = useState(
    Array.from({ length: 30 }, (_, i) => ({
      day: i + 1,
      value: 850000 + Math.random() * 50000 + i * 2500
    }))
  );

  const loadData = useCallback(async () => {
    try {
      const results = await Promise.allSettled([
        stockApi.screener({}),
        portfolioApi.get(),
        watchlistApi.getAll(),
        newsApi.get()
      ]);
      
      if (results[0].status === 'fulfilled') setStocks(results[0].value.data.slice(0, 10));
      if (results[1].status === 'fulfilled') setPortfolio(results[1].value.data);
      if (results[2].status === 'fulfilled') setWatchlists(results[2].value.data);
      if (results[3].status === 'fulfilled') setNews(results[3].value.data);
    } catch (e) {
      console.error('dashboard load failed', e);
    }
  }, []);

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user, loadData]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A0B0E' }}>
        <div className="text-white text-sm">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  return (
    <Layout>
      <div data-testid="dashboard" className="w-full max-w-[1920px] mx-auto px-6 py-6">
        {/* Nifty/Sensex Indices Ticker */}
        <IndicesTicker />
        <WatchlistTicker />

        {/* Portfolio Value Chart */}
        <div className="mb-6">
          <h2 className="text-2xl font-heading font-bold tracking-tight text-white mb-4">Portfolio Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-4">
            <div className="md:col-span-2 xl:col-span-3 bg-[#12141A] border border-white/10 p-6" data-testid="portfolio-chart">
              <div className="mb-4">
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8] mb-1">Total Value</div>
                <div className="text-3xl font-mono font-semibold text-white">
                  {formatINR(chartData[chartData.length - 1].value)}
                </div>
                <div className="text-sm text-[#00FF66] mt-1">+12.34% (+₹1,05,234.56)</div>
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#00FF66" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#00FF66" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="day" stroke="#64748B" style={CHART_AXIS_STYLE} />
                  <YAxis stroke="#64748B" style={CHART_AXIS_STYLE} />
                  <Tooltip
                    contentStyle={CHART_TOOLTIP_CONTENT_STYLE}
                    labelStyle={CHART_TOOLTIP_LABEL_STYLE}
                    itemStyle={CHART_TOOLTIP_ITEM_STYLE}
                  />
                  <Area type="monotone" dataKey="value" stroke="#00FF66" strokeWidth={2} fillOpacity={1} fill="url(#colorValue)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-[#12141A] border border-white/10 p-6" data-testid="portfolio-stats">
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8] mb-4">Quick Stats</div>
              <div className="space-y-4">
                <div>
                  <div className="text-xs text-[#94A3B8]">Holdings</div>
                  <div className="text-2xl font-mono font-semibold text-white">
                    {portfolio?.holdings?.length || 0}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-[#94A3B8]">Watchlists</div>
                  <div className="text-2xl font-mono font-semibold text-white">
                    {watchlists.length}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-[#94A3B8]">Day Change</div>
                  <div className="text-2xl font-mono font-semibold text-[#00FF66]">+2.4%</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Market Movers & News */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Top Movers */}
          <div className="bg-[#12141A] border border-white/10 p-6" data-testid="market-movers">
            <h3 className="text-xl font-heading font-bold tracking-tight text-white mb-4">Top Movers</h3>
            <div className="space-y-3">
              {stocks.slice(0, 5).map((stock) => (
                <Link
                  to={`/stock/${stock.symbol}`}
                  key={stock.symbol}
                  className="flex items-center justify-between border-b border-white/5 pb-3 hover:bg-white/[0.02] transition-colors px-2 -mx-2"
                  data-testid={`stock-item-${stock.symbol}`}
                >
                  <div>
                    <div className="font-mono font-semibold text-white text-sm">{stock.symbol}</div>
                    <div className="text-xs text-[#64748B]">{stock.name}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-white text-sm">{formatINR(stock.price)}</div>
                    <div className={`text-xs font-mono ${stock.change >= 0 ? 'text-[#00FF66]' : 'text-[#FF3366]'}`}>
                      {formatPercent(stock.change_percent)}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* News Feed */}
          <div className="bg-[#12141A] border border-white/10 p-6" data-testid="news-feed">
            <h3 className="text-xl font-heading font-bold tracking-tight text-white mb-4">
              <Newspaper size={24} weight="duotone" className="inline mr-2" />
              Market News
            </h3>
            <div className="space-y-4">
              {news.map((item) => (
                <div key={item.id} className="border-b border-white/5 pb-3" data-testid={`news-item-${item.id}`}>
                  <h4 className="text-sm font-medium text-white mb-1">{item.title}</h4>
                  <p className="text-xs text-[#94A3B8] mb-2">{item.summary}</p>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-mono ${item.sentiment === 'positive' ? 'text-[#00FF66]' : item.sentiment === 'negative' ? 'text-[#FF3366]' : 'text-[#FBBF24]'}`}>
                      {item.sentiment}
                    </span>
                    {item.source && <span className="text-xs text-[#64748B]">• {item.source}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* FII/DII + Sector Heat Map */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <FIIDIIWidget />
          <SectorBreakdown />
        </div>
      </div>
    </Layout>
  );
}